import React, { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { LoaderCircle } from "lucide-react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import MobileBottomNav from "./MobileBottomNav";
import TrialBanner from "@/components/TrialBanner";
import TaxSetupBanner from "@/components/TaxSetupBanner";
import WorkspaceThemeApplier from "@/components/WorkspaceThemeApplier";
import { OfficeProvider } from "@/context/OfficeContext";
import OfflineStatusBanner from "./OfflineStatusBanner";

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState(false);
  const location = useLocation();
  const hideMobileSidebar = location.pathname.startsWith("/microfinance") || location.pathname.startsWith("/field");
  const routeKey = `${location.pathname}${location.search}`;
  const previousRoute = useRef(routeKey);
  const loadingTimer = useRef(null);

  const showNavigationLoading = useCallback(() => {
    setNavigationLoading(true);
    window.clearTimeout(loadingTimer.current);
    loadingTimer.current = window.setTimeout(() => setNavigationLoading(false), 1100);
  }, []);

  const handleNavigationIntent = useCallback((event) => {
    const target = event.target;
    const tab = target?.closest?.('[role="tab"]');
    if (tab) {
      showNavigationLoading();
      return;
    }

    const anchor = target?.closest?.("a[href]");
    if (
      !anchor || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey ||
      event.altKey || anchor.target === "_blank" || anchor.hasAttribute("download")
    ) return;

    const destination = new URL(anchor.href, window.location.href);
    if (destination.origin === window.location.origin && `${destination.pathname}${destination.search}` !== routeKey) {
      showNavigationLoading();
    }
  }, [routeKey, showNavigationLoading]);

  useEffect(() => {
    if (previousRoute.current === routeKey) return undefined;
    previousRoute.current = routeKey;
    showNavigationLoading();
    return () => window.clearTimeout(loadingTimer.current);
  }, [routeKey, showNavigationLoading]);

  useEffect(() => () => window.clearTimeout(loadingTimer.current), []);

  return (
    <OfficeProvider>
    <WorkspaceThemeApplier />
    <div className="min-h-screen flex bg-background text-foreground" onClickCapture={handleNavigationIntent}>
      {navigationLoading && (
        <div className="fixed inset-x-0 top-0 z-[100] h-1 overflow-hidden bg-brand/20" aria-hidden="true">
          <div className="route-loading-sweep h-full w-1/3 bg-brand" />
        </div>
      )}
      {navigationLoading && (
        <div className="navigation-loading-indicator" role="status" aria-live="polite">
          <LoaderCircle className="h-4 w-4 animate-spin text-brand" />
          <span>Loading</span>
        </div>
      )}
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
        />
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar onMenuClick={() => setSidebarOpen(true)} showMenuButton={!hideMobileSidebar} />
        <main className="flex-1 px-[10px] pt-[10px] pb-20 md:pt-[10px] lg:pt-[10px] lg:pb-8 max-w-screen-2xl w-full mx-auto animate-fade-in space-y-[10px]">
          <TrialBanner />
          <TaxSetupBanner />
          <OfflineStatusBanner />
          <Outlet />
        </main>
      </div>
      {!location.pathname.startsWith("/microfinance") && <MobileBottomNav />}
    </div>
    </OfficeProvider>
  );
}
